import React, { useEffect, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Label,
    FormGroup,
    Modal,
    ModalHeader,
    ModalBody,
    Table
} from "reactstrap";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import { Button } from "reactstrap";
import { getDayReport, getOperatorDropdown, getOperatorUsers } from "../../../helpers/apis";
import { DayReportColumns } from "../../../Components/Common/columns/PnLCols";
import TransactionTab from "../PnL/TransactionTab";

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const yesterdayStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const last7DaysStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const thisMonthStartStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const DayReport = () => {
    const [loading, setLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [data, setData] = useState({ rounds: [], summary: {}, pagination: {} });
    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        gameType: "all",
        startDate: "",
        endDate: "",
        operatorId: "",
        userAcNumber: ""
    });

    const [operators, setOperators] = useState([]);
    const [users, setUsers] = useState([]);
    const [dateRange, setDateRange] = useState([]);

    // Winners Modal State
    const [winnersModal, setWinnersModal] = useState(false);
    const [winnersData, setWinnersData] = useState([]);
    const [currentRound, setCurrentRound] = useState(null);

    // Fetch operators for dropdown
    const fetchOperators = async () => {
        try {
            const res = await getOperatorDropdown();
            if (res && res.status === 1) {
                const options = (res.data || []).map(op => ({
                    value: op._id,
                    label: op.username
                }));
                setOperators(options);
            }
        } catch (err) {
            console.error("Error fetching operators", err);
        }
    };

    // Fetch users for specific operator
    const fetchUsers = async (operatorId) => {
        if (!operatorId) {
            setUsers([]);
            return;
        }
        try {
            const res = await getOperatorUsers(operatorId);
            if (res && res.data) {
                const userList = res.data.users || (Array.isArray(res.data) ? res.data : []);
                setUsers(userList);
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error("Error fetching operator users", err);
            setUsers([]);
        }
    };

    const fetchData = useCallback(async (newParams = {}) => {
        setLoading(true);
        try {
            const combinedParams = { ...params, ...newParams };
            const res = await getDayReport(combinedParams);
            if (res && res.status === 1) {
                setData(res.data);
                setParams(combinedParams);
            } else {
                toast.error(res?.message || "Failed to fetch day report");
            }
        } catch (err) {
            console.error("Error fetching day report", err);
            toast.error(err.response?.data?.message || err.message || "Error fetching day report");
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => {
        fetchOperators();
    }, []);

    useEffect(() => {
        fetchData({ page: 1 });
    }, [params.operatorId, params.userAcNumber, params.gameType, params.startDate, params.endDate]);

    const handleFilterChange = (field, value) => {
        const newParams = { ...params, [field]: value, page: 1 };

        // If operator changes, fetch its users and reset selected user
        if (field === "operatorId") {
            fetchUsers(value);
            newParams.userAcNumber = ""; // Reset user selection
        }

        setParams(newParams);
        // We don't call fetchData here anymore to match the "Manual Filter" behavior of Transaction Tab
    };

    const handleFilter = () => {
        fetchData({ page: 1 });
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const res = await getDayReport({ ...params, export: 'excel' });
            if (res && res.status === 1 && res.data) {
                const { exportData, filename, summary, columns: exportColumns } = res.data;

                // 1. Create Summary Data (Array of Arrays)
                const wsData = [
                    ["DAY REPORT SUMMARY"],
                    ["Total Bets", summary?.totalBetsAmount || 0],
                    ["Total Payout", summary?.totalPayoutAmount || 0],
                    ["Admin Profit", summary?.adminProfit || 0],
                    ["Total Rounds", summary?.totalRounds || 0],
                    [], // Blank Row
                ];

                // 2. Add Table Headers from backend columns
                if (exportColumns && Array.isArray(exportColumns)) {
                    wsData.push(exportColumns.map(c => c.header));

                    // 3. Add Table Data mapping by keys
                    exportData.forEach(item => {
                        wsData.push(exportColumns.map(c => item[c.key] ?? ''));
                    });
                } else if (exportData.length > 0) {
                    // Fallback to keys if columns missing
                    wsData.push(Object.keys(exportData[0]));
                    exportData.forEach(item => wsData.push(Object.values(item)));
                }

                const ws = XLSX.utils.aoa_to_sheet(wsData);

                // 4. Manage Column Widths
                if (exportColumns && Array.isArray(exportColumns)) {
                    ws['!cols'] = exportColumns.map(c => ({ wch: c.width || 15 }));
                }

                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Report");

                XLSX.writeFile(wb, filename || `day-report.xlsx`);
                toast.success("Excel export successful");
            } else {
                toast.error(res?.message || "Failed to export data");
            }
        } catch (err) {
            console.error("Export error", err);
            toast.error("Error during export");
        } finally {
            setIsExporting(false);
        }
    };

    const handleShowWinners = (winners, roundNum) => {
        setWinnersData(winners);
        setCurrentRound(roundNum);
        setWinnersModal(true);
    };

    const toggleWinnersModal = () => {
        setWinnersModal(!winnersModal);
    };

    const gameOptions = [
        { value: "all", label: "All Games" },
        { value: "lt", label: "Lucky Target" },
        { value: "lt1", label: "Lucky Target 1" },
        { value: "lt3", label: "Lucky Target 2" },
        { value: "ro", label: "American Roulette" },
        { value: "so", label: "Sorat" }
    ];

    const summaryMetrics = [
        { label: "Total Bets", value: data.summary?.totalBetsAmount || 0, color: "primary" },
        { label: "Total Payout", value: data.summary?.totalPayoutAmount || 0, color: "warning" },
        { label: "Admin Profit", value: data.summary?.adminProfit || 0, color: "success" },
        { label: "Total Rounds", value: data.summary?.totalRounds || 0, color: "info" }
    ];

    const customFiltersSection = (
        <div className="d-flex align-items-center gap-2">
            <Flatpickr
                className="form-control"
                placeholder="Start Date to End Date"
                options={{
                    mode: "range",
                    dateFormat: "Y-m-d"
                }}
                value={dateRange}
                onChange={(selectedDates) => {
                    setDateRange(selectedDates);
                    if (selectedDates.length === 2) {
                        const formatDate = (d) => {
                            let month = '' + (d.getMonth() + 1);
                            let day = '' + d.getDate();
                            const year = d.getFullYear();
                            if (month.length < 2) month = '0' + month;
                            if (day.length < 2) day = '0' + day;
                            return [year, month, day].join('-');
                        };
                        setParams(prev => ({
                            ...prev,
                            startDate: formatDate(selectedDates[0]),
                            endDate: formatDate(selectedDates[1]),
                            page: 1
                        }));
                    } else if (selectedDates.length === 0) {
                        setParams(prev => ({
                            ...prev,
                            startDate: "",
                            endDate: "",
                            page: 1
                        }));
                    }
                }}
                style={{ width: "210px" }}
            />

            <select
                className="form-select"
                style={{
                    width: "150px"
                }}
                value={
                    params.startDate === todayStr() && params.endDate === todayStr() ? "Today" :
                        params.startDate === yesterdayStr() && params.endDate === yesterdayStr() ? "Yesterday" :
                            params.startDate === last7DaysStr() && params.endDate === todayStr() ? "7 Days" :
                                params.startDate === thisMonthStartStr() && params.endDate === todayStr() ? "This Month" : "Custom Range"
                }
                onChange={(e) => {
                    const val = e.target.value;
                    let start = params.startDate, end = params.endDate;
                    if (val === "Today") { start = todayStr(); end = todayStr(); }
                    else if (val === "Yesterday") { start = yesterdayStr(); end = yesterdayStr(); }
                    else if (val === "7 Days") { start = last7DaysStr(); end = todayStr(); }
                    else if (val === "This Month") { start = thisMonthStartStr(); end = todayStr(); }
                    else { return; }

                    setParams(prev => ({
                        ...prev,
                        startDate: start,
                        endDate: end,
                        page: 1
                    }));
                    setDateRange([new Date(start), new Date(end)]);
                }}
            >
                <option value="Custom Range">Custom Range</option>
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="7 Days">Last 7 Days</option>
                <option value="This Month">This Month</option>
            </select>

            <select
                className="form-select"
                style={{ width: "160px" }}
                value={params.gameType}
                onChange={(e) => handleFilterChange("gameType", e.target.value)}
            >
                {gameOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>

            <select
                className="form-select"
                style={{ width: "160px" }}
                value={params.operatorId}
                onChange={(e) => handleFilterChange("operatorId", e.target.value)}
            >
                <option value="">All Operators</option>
                {operators.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                ))}
            </select>

            {params.operatorId && (
                <select
                    className="form-select"
                    style={{ width: "160px" }}
                    value={params.userAcNumber}
                    onChange={(e) => handleFilterChange("userAcNumber", e.target.value)}
                >
                    <option value="">All Users</option>
                    {Array.isArray(users) && users.map(u => (
                        <option key={u._id} value={u.acnumber}>{u.acnumber || u.email || u.username}</option>
                    ))}
                </select>
            )}

            <Button color="primary" onClick={handleFilter}>
                Filter
            </Button>
        </div>
    );

    const filterHeader = (
        <div className="d-flex align-items-center gap-2">
            {customFiltersSection}
        </div>
    );


    return (
        <div className="page-content">
            <Container fluid>
                <div className="page-title-box d-flex align-items-center justify-content-between">
                    <h4 className="mb-0">Day Report</h4>
                </div>
                <div className="mt-5 mb-5">
                    <Card>
                        <CardBody>
                            <TransactionTab
                                summaryMetrics={summaryMetrics}
                                columns={DayReportColumns(handleShowWinners)}
                                data={data.rounds || []}
                                loading={loading}
                                totalRows={data.pagination?.total || 0}
                                params={params}
                                fetchData={(p) => fetchData({ ...p })}
                                onRefresh={handleFilter}
                                onExport={handleExport}
                                isExporting={isExporting}
                                customFilters={filterHeader}
                            />
                        </CardBody>
                    </Card>
                </div>
                {/* Winners Modal */}
                <Modal isOpen={winnersModal} toggle={toggleWinnersModal} size="lg">
                    <ModalHeader toggle={toggleWinnersModal}>
                        Round #{currentRound} Winners
                    </ModalHeader>
                    <ModalBody>
                        <div className="table-responsive">
                            <Table className="table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Account ID</th>
                                        <th>Bet Amount</th>
                                        <th>Won Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {winnersData.map((winner, idx) => (
                                        <tr key={idx}>
                                            <td className="fw-bold">{winner.accountId || winner.acnumber || '-'}</td>
                                            <td>{winner.betAmount}</td>
                                            <td className="text-success fw-bold">{winner.wonAmount}</td>
                                        </tr>
                                    ))}
                                    {winnersData.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="text-center">No winners found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </ModalBody>
                </Modal>
            </Container >
        </div >
    );
};

export default DayReport;
